/*
 * SICart — SimplyInked-owned cart helper using Shopify's native Ajax Cart API.
 * Replaces the cart functions previously provided by a third-party SDK.
 *
 *   SICart.add(items, opts)        -> Promise<cart add response>
 *   SICart.addAndOpen(items, opts) -> Promise<{ ok: boolean, data?, error? }>
 *   SICart.get()                   -> Promise<cart>
 *   SICart.formatMoney(cents)      -> "$12.34" (store currency)
 *
 * `items` may be one { id, quantity, properties? } object or an array of them.
 * After a successful add the theme is told to refresh (`cart:refresh`), and the
 * cart drawer is opened: HS Slide Cart opens itself when it sees the request;
 * otherwise the theme's own <cart-drawer> is shown.
 */
(function () {
  if (window.SICart) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function normalize(items) {
    var list = Array.isArray(items) ? items : [items];
    return list
      .filter(function (i) { return i && i.id; })
      .map(function (i) {
        var line = { id: Number(i.id || i.variantId), quantity: Number(i.quantity || 1) };
        if (i.properties) line.properties = i.properties;
        if (i.selling_plan) line.selling_plan = i.selling_plan;
        return line;
      });
  }

  function emit(name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} })); } catch (e) {}
  }

  async function add(items) {
    var lines = normalize(items);
    if (!lines.length) throw new Error('No items to add');
    var res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: lines })
    });
    var data = await res.json().catch(function () { return {}; });
    if (!res.ok) {
      var msg = (data && (data.description || data.message)) || ('Cart add failed: ' + res.status);
      throw new Error(msg);
    }
    return data;
  }

  async function get() {
    var res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  function openDrawer() {
    if (window.HSSLIDECART || window.Hs_CartDrawer) return; // HS Slide Cart opens itself
    var drawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      try { drawer.show(); return; } catch (e) {}
    }
    window.location.href = root + 'cart';
  }

  async function addAndOpen(items) {
    try {
      var data = await add(items);
      // `cart:refresh` makes the theme re-fetch the cart and its drawer sections.
      // (Don't emit `cart:change`: the theme expects section HTML in that event.)
      emit('cart:refresh', {});
      emit('si-cart:added', { items: normalize(items), response: data });
      openDrawer();
      return { ok: true, data: data };
    } catch (err) {
      return { ok: false, error: { message: err && err.message ? err.message : String(err) } };
    }
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency }).format(Number(cents) / 100);
    } catch (e) {
      return '$' + (Number(cents) / 100).toFixed(2);
    }
  }

  async function getByHandle(handle) {
    var res = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Product not found: ' + handle);
    return res.json();
  }

  // Guard: the theme's cart listeners require `detail.cart` on `cart:change`.
  // Some legacy product-page blocks fire a bare `cart:change` (alongside a
  // valid `cart:refresh`), which throws in the cart count, free-shipping bar
  // and cart drawer. Swallow only those malformed events; the theme's own
  // events (which carry the cart) pass through untouched.
  window.addEventListener('cart:change', function (e) {
    if (e.detail && e.detail.cart) return;
    e.stopImmediatePropagation();
  }, true);

  window.SICart = { add: add, addAndOpen: addAndOpen, get: get, formatMoney: formatMoney, getByHandle: getByHandle };

  // Release 1 compatibility: the product-page blocks generated by the former
  // vendor still look for window.Spectrum. Answer those calls with native
  // Shopify behaviour so the blocks keep working with no vendor code loaded.
  // Remove when the blocks are rebuilt as SimplyInked sections (Release 2).
  if (!window.Spectrum) {
    window.Spectrum = {
      cart: {
        add: function (items, opts) { return addWithSections(items, opts && opts.sections); },
        addAndOpen: addAndOpen,
        get: get
      },
      products: { getByHandle: getByHandle },
      formatMoney: formatMoney,
      money: { format: function (cents) { return formatMoney(cents); } }
    };
  }

  async function addWithSections(items, sections) {
    if (!sections) return add(items);
    var res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: normalize(items), sections: sections })
    });
    var data = await res.json().catch(function () { return {}; });
    if (!res.ok) throw new Error((data && (data.description || data.message)) || ('Cart add failed: ' + res.status));
    return data;
  }
})();
